import { type CreatePackageHotelRequest, type PackageHotelOut } from '@utazzo/shared';
import { UzForm, UzFormField, UzInput, UzNumberInput } from '@utazzo/ui';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { PackageFormPanel } from './PackageFormPanel';

interface FormValues {
  cityName: string | null;
  hotelName: string | null;
  category: string | null;
  nights: number | null;
  occupancy: string | null;
}

interface Props {
  mode: 'create' | 'edit';
  item?: PackageHotelOut;
  pending: boolean;
  onClose: () => void;
  onSubmit: (payload: CreatePackageHotelRequest) => Promise<void>;
}

const toDefaults = (mode: 'create' | 'edit', item: PackageHotelOut | undefined): FormValues =>
  mode === 'edit' && item
    ? {
        cityName: item.cityName,
        hotelName: item.hotelName,
        category: item.category,
        nights: item.nights,
        occupancy: item.occupancy,
      }
    : {
        cityName: null,
        hotelName: null,
        category: null,
        nights: null,
        occupancy: null,
      };

const trimOrNull = (v: string | null | undefined): string | null => {
  if (v === null || v === undefined) return null;
  const t = v.trim();
  return t === '' ? null : t;
};

export const PackageHotelFormDrawer = ({
  mode,
  item,
  pending,
  onClose,
  onSubmit,
}: Props): React.JSX.Element => {
  const form = useForm<FormValues>({ defaultValues: toDefaults(mode, item) });

  useEffect(() => {
    form.reset(toDefaults(mode, item));
  }, [mode, item, form]);

  const submit = form.handleSubmit(async (values) => {
    await onSubmit({
      cityName: trimOrNull(values.cityName),
      hotelName: trimOrNull(values.hotelName),
      category: trimOrNull(values.category),
      nights: values.nights,
      occupancy: trimOrNull(values.occupancy),
    });
  });

  return (
    <PackageFormPanel
      title={mode === 'create' ? 'Add hotel' : 'Edit hotel'}
      description="Hotel stay details for this package."
      pending={pending}
      submitLabel={mode === 'create' ? 'Add hotel' : 'Save changes'}
      onClose={onClose}
      onSubmit={submit}
    >
      <UzForm form={form} onSubmit={submit}>
        <div className="space-y-5">
          <UzFormField name="hotelName">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null | undefined) ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                label="Hotel name"
                placeholder="e.g. The Grand Palace Hotel"
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>

          <UzFormField name="cityName">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null | undefined) ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                label="City"
                placeholder="e.g. Dubai"
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <UzFormField name="category">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null | undefined) ?? ''}
                  onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                  label="Category"
                  placeholder="5-star"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>

            <UzFormField name="nights">
              {({ field, error }) => (
                <UzNumberInput
                  name={field.name}
                  label="Nights"
                  precision={0}
                  min={0}
                  max={365}
                  value={
                    field.value === null || field.value === undefined
                      ? undefined
                      : (field.value as number)
                  }
                  onValueChange={(v) => field.onChange(v ?? null)}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
          </div>

          <UzFormField name="occupancy">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null | undefined) ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                label="Occupancy"
                placeholder="Double on Twin"
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
        </div>
      </UzForm>
    </PackageFormPanel>
  );
};
